/**
 * MVA Dashboard Component
 *
 * Comprehensive exit planning dashboard showing:
 * - Executive summary
 * - Buyer type analysis
 * - Deal structure comparison
 * - Net proceeds analysis
 * - Earnout and seller financing scenarios
 */

import React, { useState, useEffect } from 'react';
import BuyerAnalysisCard from './BuyerAnalysisCard';
import DealStructureComparison from './DealStructureComparison';
import NetProceedsWaterfall from './NetProceedsWaterfall';
import EarnoutScenarios from './EarnoutScenarios';
import SellerFinancingAnalysis from './SellerFinancingAnalysis';
import { calculateMVA } from '../../services/api';
import './MVADashboard.css';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

function MVADashboard({ result, vacResult, valuationId, userId, onRecalculate, onMVAComplete, onBack, onExport }) {
  const [activeTab, setActiveTab] = useState('summary');
  const [mvaData, setMvaData] = useState(result);
  const [loading, setLoading] = useState(!result && vacResult);
  const [error, setError] = useState(null);

  // MVA Configuration State
  const [mvaConfig, setMvaConfig] = useState({
    entityType: 's_corp',
    holdingPeriod: 60,
    isQSBS: false,
    earnoutPercent: 0,
    sellerFinancingPercent: 0
  });

  // Calculate MVA when we have VAC result but no MVA result
  useEffect(() => {
    if (vacResult && !result && !mvaData) {
      calculateMVAFromVAC();
    }
  }, [vacResult, result]);

  const calculateMVAFromVAC = async () => {
    setLoading(true);
    setError(null);

    try {
      // Build MVA input from VAC result
      const mvaInput = {
        riskAnswers: vacResult.riskAnswers || {},
        financials: {
          revenue: vacResult.revenue || 0,
          pretaxProfit: vacResult.ebitda || vacResult.ebitdaAdjusted || 0,
          depreciation: 0,
          interest: 0,
          discretionary: 0
        },
        entityType: mvaConfig.entityType,
        holdingPeriod: mvaConfig.holdingPeriod,
        isQSBS: mvaConfig.isQSBS,
        earnoutPercent: mvaConfig.earnoutPercent,
        sellerFinancingPercent: mvaConfig.sellerFinancingPercent,
        valuationId,
        userId
      };

      const response = await calculateMVA(mvaInput);

      if (response.success) {
        setMvaData(response.result);
        if (onMVAComplete) {
          onMVAComplete(response.result);
        }
      } else {
        setError(response.error || 'Failed to calculate MVA');
      }
    } catch (err) {
      console.error('MVA calculation error:', err);
      setError(err.message || 'Failed to calculate MVA');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = () => {
    setMvaData(null);
    calculateMVAFromVAC();
  };

  if (loading) {
    return (
      <div className="mva-dashboard loading">
        <div className="spinner"></div>
        <p>Calculating exit planning analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mva-dashboard error-state">
        <h2>Analysis Error</h2>
        <p>{error}</p>
        <div className="error-actions">
          <button className="btn btn-primary" onClick={calculateMVAFromVAC}>
            Try Again
          </button>
          {onBack && (
            <button className="btn btn-secondary" onClick={onBack}>
              Back to Dashboard
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!mvaData) {
    return (
      <div className="mva-dashboard empty">
        <h2>No Analysis Available</h2>
        <p>Please complete a valuation assessment first.</p>
        {onBack && (
          <button className="btn btn-primary" onClick={onBack}>
            Back to Dashboard
          </button>
        )}
      </div>
    );
  }

  const {
    executiveSummary,
    vacAnalysis,
    buyerAnalysis,
    dealStructureAnalysis,
    workingCapitalAnalysis,
    earnoutAnalysis,
    sellerFinancingAnalysis,
    netProceedsAnalysis
  } = mvaData;

  const tabs = [
    { id: 'summary', label: 'Executive Summary' },
    { id: 'buyers', label: 'Buyer Analysis' },
    { id: 'structure', label: 'Deal Structure' },
    { id: 'proceeds', label: 'Net Proceeds' },
    ...(earnoutAnalysis ? [{ id: 'earnout', label: 'Earnout' }] : []),
    ...(sellerFinancingAnalysis ? [{ id: 'financing', label: 'Seller Financing' }] : [])
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'summary':
        return (
          <div className="tab-content summary-tab">
            {/* Key Metrics Cards */}
            <div className="metrics-grid">
              <div className="metric-card primary">
                <span className="metric-label">Enterprise Value</span>
                <span className="metric-value">
                  {formatCurrency(executiveSummary.enterpriseValue)}
                </span>
              </div>
              <div className="metric-card success">
                <span className="metric-label">Estimated Net Proceeds</span>
                <span className="metric-value">
                  {formatCurrency(executiveSummary.estimatedNetProceeds)}
                </span>
                <span className="metric-subtext">
                  {executiveSummary.proceedsRetentionRate}% retention rate
                </span>
              </div>
              <div className="metric-card info">
                <span className="metric-label">Recommended Buyer</span>
                <span className="metric-value small">
                  {executiveSummary.recommendedBuyer}
                </span>
              </div>
              <div className="metric-card info">
                <span className="metric-label">Recommended Structure</span>
                <span className="metric-value small">
                  {executiveSummary.recommendedStructure}
                </span>
              </div>
            </div>

            {/* Key Findings */}
            <div className="findings-section">
              <h3>Key Findings</h3>
              <ul className="findings-list">
                {executiveSummary.keyFindings.map((finding, index) => (
                  <li key={index}>{finding}</li>
                ))}
              </ul>
            </div>

            {/* VAC Summary */}
            {vacAnalysis && (
              <div className="vac-summary">
                <h3>Valuation Analysis</h3>
                <div className="vac-metrics">
                  <div className="vac-metric">
                    <span className="label">Risk Score</span>
                    <span className="value">{vacAnalysis.riskScore}/70</span>
                  </div>
                  <div className="vac-metric">
                    <span className="label">Risk Category</span>
                    <span className={`value risk-${vacAnalysis.riskCategory}`}>
                      {vacAnalysis.riskCategory}
                    </span>
                  </div>
                  <div className="vac-metric">
                    <span className="label">Multiple Used</span>
                    <span className="value">{vacAnalysis.multipleUsed?.toFixed(2)}x</span>
                  </div>
                  <div className="vac-metric">
                    <span className="label">Adjusted EBITDA</span>
                    <span className="value">{formatCurrency(vacAnalysis.ebitdaAdjusted)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Working Capital Summary */}
            {workingCapitalAnalysis && workingCapitalAnalysis.adjustment !== 0 && (
              <div className="wc-summary">
                <h3>Working Capital Adjustment</h3>
                <p className={workingCapitalAnalysis.adjustmentType}>
                  {workingCapitalAnalysis.explanation}
                </p>
                <div className="wc-impact">
                  <span className="label">Impact on Purchase Price:</span>
                  <span className={`value ${workingCapitalAnalysis.adjustmentType}`}>
                    {workingCapitalAnalysis.adjustmentType === 'addition' ? '+' : '-'}
                    {formatCurrency(Math.abs(workingCapitalAnalysis.adjustment))}
                  </span>
                </div>
              </div>
            )}
          </div>
        );

      case 'buyers':
        return (
          <div className="tab-content buyers-tab">
            <BuyerAnalysisCard
              buyerAnalysis={buyerAnalysis}
            />
          </div>
        );

      case 'structure':
        return (
          <div className="tab-content structure-tab">
            <DealStructureComparison
              dealStructureAnalysis={dealStructureAnalysis}
            />
          </div>
        );

      case 'proceeds':
        return (
          <div className="tab-content proceeds-tab">
            <NetProceedsWaterfall
              netProceedsAnalysis={netProceedsAnalysis}
            />
          </div>
        );

      case 'earnout':
        return earnoutAnalysis ? (
          <div className="tab-content earnout-tab">
            <EarnoutScenarios
              earnoutAnalysis={earnoutAnalysis}
            />
          </div>
        ) : null;

      case 'financing':
        return sellerFinancingAnalysis ? (
          <div className="tab-content financing-tab">
            <SellerFinancingAnalysis
              sellerFinancingAnalysis={sellerFinancingAnalysis}
            />
          </div>
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className="mva-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Exit Planning Analysis</h1>
          <p className="subtitle">Market Value Assessment (MVA)</p>
        </div>
        <div className="header-actions">
          {onBack && (
            <button className="btn btn-secondary" onClick={onBack}>
              Back to Dashboard
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleRecalculate}>
            Recalculate
          </button>
          {onExport && (
            <button className="btn btn-primary" onClick={onExport}>
              Export Report
            </button>
          )}
        </div>
      </header>

      <nav className="dashboard-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main className="dashboard-main">
        {renderTabContent()}
      </main>

      <footer className="dashboard-footer">
        <p className="disclaimer">
          This analysis is for informational purposes only and should not be considered financial,
          legal, or tax advice. Consult with qualified professionals before making business decisions.
        </p>
        <p className="timestamp">
          Generated: {new Date(mvaData.timestamp).toLocaleString()}
        </p>
      </footer>
    </div>
  );
}

export default MVADashboard;
